// Re-export shared types and helpers
export type { WordTiming, KenBurnsConfig, Scene, LogoSceneConfig } from '../shared/types';
export { msToFrames, getCurrentScene, getVisibleWords, KEN_BURNS_PRESETS } from '../shared/types';
import type { WordTiming, Scene } from '../shared/types';
import { KEN_BURNS_PRESETS } from '../shared/types';

// Color palettes — Agency Distrust theme
export const SCENE_COLORS = {
  scene1: { primaryColor: '#ffffff', accentColor: '#FCD34D' },   // White/Gold - Promises
  scene2: { primaryColor: '#ffffff', accentColor: '#F59E0B' },   // White/Amber - Vanity metrics
  scene3: { primaryColor: '#EF4444', accentColor: '#ffffff' },   // Red/White - Silent phone
  scene4: { primaryColor: '#22C55E', accentColor: '#ffffff' },   // Green/White - Specialist
  scene5: { primaryColor: '#8B5CF6', accentColor: '#ffffff' },   // Purple/White - Knowledge
  scene6: { primaryColor: '#FCD34D', accentColor: '#ffffff' },   // Gold/White - CTA
  logo: { primaryColor: '#ffffff', accentColor: '#FCD34D' },
};

export const SCENE_IMAGES = {
  scene1: 'images/gdm-last-agency/scene1-promises.png',
  scene2: 'images/gdm-last-agency/scene2-vanity.png',
  scene3: 'images/gdm-last-agency/scene3-silent-phone.png',
  scene4: 'images/gdm-last-agency/scene4-specialist.png',
  scene5: 'images/gdm-last-agency/scene5-knowledge.png',
  scene6: 'images/gdm-last-agency/scene6-report.png',
};

export const REPORT_SLIDES = [
  'images/gdm-quiet-phone/report-slide-01.png',
  'images/gdm-quiet-phone/report-slide-05.png',
  'images/gdm-quiet-phone/report-slide-07.png',
  'images/gdm-quiet-phone/report-slide-12.png',
];

export const DRAMATIC_WORDS = ["didn't", 'ring.', 'only', "That's", 'Free'];

// Word timings from Whisper — manual corrections: "10" → "Ten"
export const WORD_TIMINGS: WordTiming[] = [
  // Scene 1: Promises
  { text: 'Your', startMs: 0, endMs: 120 },
  { text: 'last', startMs: 120, endMs: 300 },
  { text: 'agency', startMs: 300, endMs: 640 },
  { text: 'promised', startMs: 640, endMs: 1020 },
  { text: 'you', startMs: 1020, endMs: 1340 },
  { text: 'leads.', startMs: 1340, endMs: 1800 },

  // Scene 2: Vanity metrics
  { text: 'You', startMs: 1800, endMs: 2240 },
  { text: 'got', startMs: 2240, endMs: 2380 },
  { text: 'clicks.', startMs: 2380, endMs: 2880 },
  { text: 'You', startMs: 2880, endMs: 3280 },
  { text: 'got', startMs: 3280, endMs: 3440 },
  { text: 'impressions.', startMs: 3440, endMs: 4120 },
  { text: 'You', startMs: 4120, endMs: 4520 },
  { text: 'got', startMs: 4520, endMs: 4640 },
  { text: 'a', startMs: 4640, endMs: 4740 },
  { text: 'nice', startMs: 4740, endMs: 4900 },
  { text: 'dashboard.', startMs: 4900, endMs: 5520 },

  // Scene 3: Silent phone
  { text: 'But', startMs: 5520, endMs: 6560 },
  { text: 'your', startMs: 6560, endMs: 6700 },
  { text: 'phone', startMs: 6700, endMs: 6920 },
  { text: "didn't", startMs: 6920, endMs: 7220 },
  { text: 'ring.', startMs: 7220, endMs: 7420 },

  // Scene 4: Specialist
  { text: 'We', startMs: 7420, endMs: 7520 },
  { text: 'only', startMs: 7520, endMs: 7680 },
  { text: 'work', startMs: 7680, endMs: 7860 },
  { text: 'with', startMs: 7860, endMs: 8020 },
  { text: 'garage', startMs: 8020, endMs: 8240 },
  { text: 'door', startMs: 8240, endMs: 8460 },
  { text: 'companies.', startMs: 8460, endMs: 9040 },
  { text: "That's", startMs: 9040, endMs: 9720 },
  { text: 'it.', startMs: 9720, endMs: 9940 },
  { text: 'Nothing', startMs: 9940, endMs: 10860 },
  { text: 'else.', startMs: 10860, endMs: 11360 },

  // Scene 5: Knowledge
  { text: 'We', startMs: 11360, endMs: 12440 },
  { text: 'know', startMs: 12440, endMs: 12560 },
  { text: 'your', startMs: 12560, endMs: 12720 },
  { text: 'market.', startMs: 12720, endMs: 13140 },
  { text: 'Your', startMs: 13140, endMs: 13380 },
  { text: 'customers.', startMs: 13380, endMs: 13960 },
  { text: 'Your', startMs: 13960, endMs: 14040 },
  { text: 'competitors.', startMs: 14040, endMs: 14800 },

  // Scene 6: CTA
  { text: 'Free', startMs: 14800, endMs: 15460 },
  { text: 'Market', startMs: 15460, endMs: 15700 },
  { text: 'Report.', startMs: 15700, endMs: 16280 },
  { text: 'Ten', startMs: 16280, endMs: 16580 },
  { text: 'minutes.', startMs: 16580, endMs: 16960 },
  { text: 'Link', startMs: 16960, endMs: 17320 },
  { text: 'below.', startMs: 17320, endMs: 17760 },
];

export const SCENES: Scene[] = [
  {
    id: 1,
    name: 'Promises',
    startMs: 0,
    endMs: 1800,
    words: WORD_TIMINGS.slice(0, 6),
    ...SCENE_COLORS.scene1,
    backgroundImage: SCENE_IMAGES.scene1,
    kenBurns: KEN_BURNS_PRESETS.zoomIn,
  },
  {
    id: 2,
    name: 'Vanity',
    startMs: 1800,
    endMs: 5520,
    words: WORD_TIMINGS.slice(6, 17),
    ...SCENE_COLORS.scene2,
    backgroundImage: SCENE_IMAGES.scene2,
    kenBurns: KEN_BURNS_PRESETS.panRight,
  },
  {
    id: 3,
    name: 'Silent Phone',
    startMs: 5520,
    endMs: 7420,
    words: WORD_TIMINGS.slice(17, 22),
    ...SCENE_COLORS.scene3,
    backgroundImage: SCENE_IMAGES.scene3,
    kenBurns: KEN_BURNS_PRESETS.dramaticZoom,
  },
  {
    id: 4,
    name: 'Specialist',
    startMs: 7420,
    endMs: 11360,
    words: WORD_TIMINGS.slice(22, 33),
    ...SCENE_COLORS.scene4,
    backgroundImage: SCENE_IMAGES.scene4,
    kenBurns: KEN_BURNS_PRESETS.zoomOut,
  },
  {
    id: 5,
    name: 'Knowledge',
    startMs: 11360,
    endMs: 14800,
    words: WORD_TIMINGS.slice(33, 41),
    ...SCENE_COLORS.scene5,
    backgroundImage: SCENE_IMAGES.scene5,
    kenBurns: KEN_BURNS_PRESETS.panLeft,
  },
  {
    id: 6,
    name: 'CTA',
    startMs: 14800,
    endMs: 17760,
    words: WORD_TIMINGS.slice(41),
    ...SCENE_COLORS.scene6,
    backgroundImage: SCENE_IMAGES.scene6,
    kenBurns: KEN_BURNS_PRESETS.zoomWithTilt,
  },
];

export const LOGO_SCENE = {
  startMs: 17760,
  endMs: 22860,
  ...SCENE_COLORS.logo,
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
